(ns app.domain.form.schema
  (:require #?(:clj [core.util.edn :as edn])
            [app.domain.group.permit-type-config.model :as ptc-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [potpuri.core :as p]))

(defn supported-schema-types [[_enabled-flags group-features]]
  (concat [:form.schema.type/role-specific
           :form.schema.type/general
           :form.schema.type/pre-consultation
           :form.schema.type/sign-off
           :form.schema.type/work-specific
           :form.schema.type/execution]
          (when (contains? group-features :group.feature/periodic-inspections)
            ;; Periodic forms can currently be added only in configurations;
            ;; it is not possible to add them ad-hoc to workspaces
            [:form.schema.type/periodic])))

(def permit-types-with-execution-form-only
  "Permit types which don't have any other form than execution form."
  #{:permit-type/CE
    :permit-type/PWO})

(def permit-types-with-execution-form
  "Permit types that can have forms in execution phase. This should reflect
  the state machine definitions but as there is no easy way to extract
  that information, we maintain this set."
  (set/union permit-types-with-execution-form-only
             #{:permit-type/B
               :permit-type/LU
               :permit-type/P
               ;; PI child workspace allows adding execution forms
               :permit-type/PI
               :permit-type/SW}))

(defn- schema-type-filter-fn [permit-types]
  (let [has-any-permit-type? (fn [required-permit-types]
                               (seq (set/intersection permit-types required-permit-types)))]
    (fn [schema-type]
      (case schema-type
        :form.schema.type/execution
        (has-any-permit-type? permit-types-with-execution-form)

        :form.schema.type/work-specific
        (has-any-permit-type? ptc-model/permit-types-with-multiple-work-types)

        ;; By default, keep schema type unless the only available permit type
        ;; is one with execution form only
        (not (and (has-any-permit-type? permit-types-with-execution-form-only)
                  (= (count permit-types) 1)))))))

(defn schema-options [permit-types supported-schema-types]
  (filter (schema-type-filter-fn permit-types) supported-schema-types))

(defn resolve-schema-type [form-schema-entity]
  (get form-schema-entity :form.schema/type :form.schema.type/general))

(def field-tags-for-scalar-fields
  #{:input
    :textarea
    :select
    :checkbox
    :radio-button-group
    :radio-button-group-custom
    :date-picker
    :time-picker
    :ventilation-calculator
    :ventilation-input-calculator
    :ventilation-select-calculator
    :date-counter
    :area-input
    :area-ratio})

(def field-tags
  (set/union field-tags-for-scalar-fields
             #{:table :file-input :signature :personal-id}))


(def field-type->data-type {:input                     :text
                            :number-input              :number
                            :textarea                  :text
                            :select                    :text
                            :checkbox                  :boolean
                            :radio-button-group        :text
                            :radio-button-custom       :text
                            :radio-button-group-custom :text
                            :date-picker               :date
                            :time-picker               :time
                            :ventilation-calculator    :number
                            :area-ratio                :number})


(def data-type->field-types
  (-> (reduce (fn [acc [k v]]
                (update acc v #(conj % k))) {} field-type->data-type)
      ;; number input added for UI convenience even though in schema it's an input with number type
      (update :number #(conj % :number-input))))


(def values-for-unit-label
  ;; some old schemas have w as value
  ["USD" "CAD" "%" "w" "W" "amp" "GJ (1 GJ = 1000 MJ)" "º"])


(def new-values-for-unit-label
  (remove #{"w"} values-for-unit-label))


(defn produce-data [attr children]
  (p/assoc-if (select-keys attr [:form.field/validator
                                 :form.field/required
                                 :form.field/valid-values
                                 :form.field/integration-field-id
                                 :form.field/ecm-integration-field-id
                                 :form.field/integration-value-mapping
                                 :condition
                                 :signature-type
                                 :required-role
                                 :required-group
                                 :unit
                                 :label
                                 :unit-label
                                 :type
                                 :parent-fields
                                 :headers
                                 :phrases-picker?
                                 :rich-text?])
              :form.field/valid-values (->> children
                                            (filter #(= (first %) :radio-button))
                                            (map #(-> % second :value))
                                            not-empty)))

(defn- element->field-config
  "Build the \"render shape\" field config for a single hiccup form element, derived
   from its own attrs (not its descendants): the `produce-data` selection (schema
   `:form.field/*` keys plus `:unit`, `:unit-label`, `:label`, `:type`, `:rich-text?`,
   `:headers`, `:condition`, ...) with the element's `:tag` and `:field` assoc'd on.

   `:field` is included even though config maps are usually keyed by it, so that configs
   read out of a sequence (e.g. a table's `:columns`) are self-describing.

   This \"render shape\" differs from the entries produced by `form-schema->field-list`
   (the \"list/picker shape\", which instead carries `:parent-label`/`:form-label`/
   `:description` and none of the `:form.field/*`/`:unit-label`/`:rich-text?` keys). A
   table's `:columns` can therefore hold either shape depending on which builder produced
   it; see `table-columns` (render shape) and `form-schema->field-list` (list shape)."
  [tag attr children]
  (-> attr
      (produce-data children)
      (assoc :tag tag)
      (p/assoc-if :field (:field attr))))

(defn- table-columns
  "A table's columns are ordinary field elements; build the \"render shape\" field config
   (see `element->field-config`) for each. Returns an ordered vector of column configs so
   cell values render positionally against the table headers (see
   `app.domain.form.model/table-field-data-for-ws`).

   This is the render-shape `:columns` attached by `get-fields-from-schema` and `find-field`.
   Note `form-schema->field-list` also attaches a `:columns` vector to its table entry, but
   built from list/picker-shape entries — a different shape. Consumers must know which builder
   produced the map they hold."
  [children]
  (->> children
       (filter (comp keyword? first))
       (mapv (fn [[child-tag child-attr & child-children]]
               (element->field-config child-tag child-attr child-children)))))

(defn merge-duplicate-field
  "Merge two config maps for the SAME field id encountered more than once in a schema
  (typically the field duplicated across conditional branches).

  `:condition` is combined specially: both present → OR'd into a vector; exactly one present →
  dropped (a duplicate without a condition means the field is shown unconditionally, so the
  other branch's condition must not be kept). Every other key is a plain right-biased `merge`,
  i.e. the LATER occurrence wins.

  ASSUMES the two maps really describe the same field. There is no safeguard for the case where
  two *semantically different* fields happen to share an id (e.g. a scalar `:foo` and a `:foo`
  column in some table): their attributes get blended and `:tag`/`:unit`/`:label` become
  last-in-document-order-wins. Field ids are not enforced unique per schema — see
  `get-fields-from-schema`."
  [f1 f2]
  (let [merged (merge f1 f2)
        c1     (:condition f1)
        c2     (:condition f2)]
    (cond
      (and c1 c2)
      (assoc merged :condition
             (vec (concat (if (vector? c1) c1 [c1])
                          (if (vector? c2) c2 [c2]))))
      (or c1 c2) (dissoc merged :condition)
      :else merged)))

(defn safe-merge-with
  "Attribute merge may return vectors instead of maps, so make them merge-with compatible."
  [f & maps-or-pairs]
  (let [to-map (fn [m-or-p]
                 (if (map? m-or-p)
                   m-or-p
                   (into {} [m-or-p])))]
    (apply merge-with f (map to-map maps-or-pairs))))

(defn get-fields-from-schema
  "Walk a decoded form data-schema and return a map of field-id -> \"render shape\" config
  (see `element->field-config`). A `:table` field's entry additionally carries `:columns` (its
  per-column render-shape configs, from `table-columns`); the table's columns are ALSO emitted
  as their own top-level id -> config entries, so a cell can be looked up by its bare id (e.g.
  from `app.domain.form.model/field-attrs+field`).

  When the same id occurs more than once the entries are merged with `merge-duplicate-field`
  (plain keys last-in-document-order-wins, conditions OR'd). This is correct for a field
  duplicated across conditional branches, but for two *semantically different* fields that share
  an id — a scalar and a same-named table column, or same-named columns in two tables — the
  merged config is an unreliable last-wins blend. Field ids are NOT enforced unique per schema,
  so resolving anything by bare id is inherently ambiguous when such collisions exist.

  NOTE: `find-field` resolves a duplicate id to the FIRST match in document order, whereas this
  fn resolves it to the last-wins merge — the two can disagree for a colliding id."
  [[tag {:keys [field condition parent-fields] :as attr} & children]]
  (apply safe-merge-with
         merge-duplicate-field
         (when field
           {field (cond-> (element->field-config tag attr children)
                    (= tag :table) (assoc :columns (table-columns children)))})
         ; Associates and merges condition from parent if not ,
         ; nil - schema may have condition set in the parent of a field
         (let [merge-attr (fn [[key child-attr & child-vecs]]
                            (let [child-condition (:condition child-attr)
                                  merged-cond     (cond
                                                    (map? condition)
                                                    (->> (merge (:field child-condition)
                                                                (:field condition))
                                                         (hash-map :field)
                                                         (hash-map :condition))
                                                    ;; if lower level has no condition, use upper even if it is
                                                    ;; an OR condition, which is difficult to merge
                                                    (and condition (nil? child-condition))
                                                    {:condition condition})
                                  parent-fields   (when (or parent-fields field)
                                                    (->> (if field
                                                           (conj (or parent-fields []) field)
                                                           parent-fields)
                                                         (hash-map :parent-fields)))]
                              (-> (vector key
                                          (merge child-attr merged-cond parent-fields))
                                  (into child-vecs)
                                  get-fields-from-schema)))]
           (cond
             (keyword? (ffirst children))
             (mapcat merge-attr children)

             ;; Support for vector of elements, e.g. `[[:input] [:p]]`, for rows, etc
             (vector? (ffirst children))
             (->> children
                  (mapcat #(mapcat merge-attr %)))

             :else
             nil))))

(defn merge-conditions [additional-condition root-condition]
  (cond
    ;; AND condition on root and branch - marge
    (and (map? additional-condition)
         (map? root-condition))
    (merge-with merge additional-condition root-condition)
    ;; OR condition on root, branch or both - assume branch condition represents
    ;; the AND of those two, as this is how it has been historically implemented
    (and additional-condition root-condition)
    additional-condition
    ;; only one of root or branch has condition - return that
    :else (or additional-condition root-condition)))

(defn find-field
  "Resolve `id-to-find` in a decoded data-schema to its \"render shape\" config
   (see `element->field-config`) with `:condition` merged from up-tree, or nil if not found.

   Resolution is positional: the schema is walked pre-order, left-to-right, and the FIRST
   element whose `:field` equals `id-to-find` wins. There is no notion of preferring a
   top-level field over a nested one — whichever appears first in document order is returned.
   Because field ids are not unique per schema, a duplicated id (a scalar and a same-named table
   column, or same-named columns in two tables) may resolve to a different occurrence than
   intended. This disagrees with `get-fields-from-schema`, which merges duplicates last-wins
   rather than taking the first.

   Table columns are ordinary field elements, so a column id (a cell field nested in a `:table`)
   resolves to that column's config directly, without the table being involved; such a result
   carries no `:columns` and no back-reference to its table. A `:table` id resolves to the table
   config, which does carry `:columns` (its per-column render-shape configs)."
  ([data-schema id-to-find] (find-field nil data-schema id-to-find))
  ([parent-conditions [tag {:keys [field condition] :as attr} & children] id-to-find]
   (when id-to-find
     (let [merged-condition         (merge-conditions condition parent-conditions)
           find-field-from-children (fn [children]
                                      (some #(find-field merged-condition % id-to-find)
                                            children))]
       (if (= field id-to-find)
         (cond-> (element->field-config tag (p/assoc-if attr :condition merged-condition) children)
           (= tag :table) (assoc :columns (table-columns children)))
         (or (->> children
                  (filter (comp keyword? first))
                  find-field-from-children)
             ;; Support for vector of elements, e.g. `[[:input] [:p]]`, for rows, etc
             (->> children
                  (filter (comp vector? first))
                  (some find-field-from-children))))))))

(defn remove-nil-schema-elements
  "Remove nil elements from form schema, allowing us to use `(when condition [:form-element {} ...])`
  in their definition."
  [[element-name & [opts & children] :as element]]
  (if opts
    (into [element-name opts]
          (->> children
               (map (fn [child]
                      (if (vector? child)
                        (remove-nil-schema-elements child)
                        child)))
               (remove nil?)))
    element))

(defn data-schema-has-content? [data-schema]
  (boolean (some-> data-schema
                   count
                   (> 2))))

(defn remove-group-id-from-id [schema-id]
  (-> schema-id
      (str/split #"/" 2)
      last))

(defn form-schema->field-list
  "Go through a list of form schemas and pick all fields in a flat list
   with entries like
   ```
   {:tag :input
    :field :area-of-work
    :label \"Area of work\"
    :form-label \"Application for a Permit to Construct or Demolish\"
    :unit :m2}
   ```
   Attaches the `form-label` argument to every field. When encountering
   a checkbox-group it is handled specially: every checkbox inside
   checkbox-group gets the checkbox-group's label prepended to it's
   label, separated by a dash.

   A table is listed as a single entry (`:tag :table`) whose `:columns` holds the
   list of its column fields; the columns are NOT also emitted as top-level fields.
   Unlike scalar fields a table needs no label to be listed. Callers that care about
   columns read `:columns`; callers that don't can ignore or filter out the table
   entry by its `:table` tag.

   These entries (and the column entries nested under a table's `:columns`) are the
   \"list/picker shape\": `:tag`, `:field`, `:label`, `:parent-label`, `:form-label`,
   `:description`, `:unit`, `:type`, `:form.field/valid-values`. This is NOT the same shape as
   the `:columns` attached by `get-fields-from-schema`/`find-field` (the \"render shape\", which
   carries `:unit-label`, `:rich-text?`, `:headers`, `:form.field/*` and no `:parent-label`/
   `:form-label`). Consumers must know which builder produced the map they hold."
  ([schemas form-label]
   (form-schema->field-list schemas form-label [] nil))
  ([schemas form-label fields parent-label]
   ;; A field may be defined in several places within one schema (e.g. duplicated
   ;; across conditional branches), which flattens to repeated identical entries;
   ;; de-duplicate so the list has one entry per distinct field.
   (distinct
     (flatten
       (for [schema schemas]
         (cond
           ;; Check if we're processing the children of a form-element,
           ;; which stores its children in a vector (e.g. :structured-list)
           (and (vector? schema) (vector? (first schema)))
           (form-schema->field-list schema form-label fields parent-label)
           ;; or just a normal hiccup-style form element
           (vector? schema)
           (let [[tag attrs & children] schema
                 children?              (seq children)
                 table?                 (= tag :table)
                 field?                 (and (some? (:field attrs))
                                             (or (some? (:label attrs))
                                                 table?))
                 last-label             (or (:label attrs) parent-label)
                 checkbox-group?        (= tag :checkbox-group)
                 new-fields             (cond
                                          checkbox-group?
                                          (conj fields (map (fn [[tag {:keys [field description label unit]}]]
                                                              {:tag          tag
                                                               :field        field
                                                               :description  description
                                                               :label        (str last-label " - " label)
                                                               :parent-label last-label
                                                               :form-label   form-label
                                                               :unit         unit})
                                                            children))
                                          field?
                                          (conj fields (cond-> (merge (select-keys attrs [:field :description :label :form.field/valid-values :type :unit])
                                                                      {:tag          tag
                                                                       :parent-label parent-label
                                                                       :form-label   form-label})
                                                         ;; A table usually has no :label of its own; fall back to a
                                                         ;; readable name from its field id so it displays, sorts,
                                                         ;; groups and searches sensibly instead of as a nil label.
                                                         (and table? (nil? (:label attrs)))
                                                         (assoc :label (some-> attrs :field name))
                                                         ;; Columns are ordinary field elements; nest them under the
                                                         ;; table instead of emitting them as separate top-level fields.
                                                         table? (assoc :columns (vec (form-schema->field-list children form-label [] last-label)))))
                                          :else
                                          fields)]
             ;; Table children are captured in :columns above, so don't also walk them
             ;; into the top-level list.
             (if (and children? (not checkbox-group?) (not table?))
               (form-schema->field-list children form-label new-fields last-label)
               new-fields))
           :else
           fields))))))

(defn schema-has-no-fields? [schema]
  (empty?
    (some->
      schema
      :form.schema/data-schema
      #?(:clj edn/decode-cached :cljs identity)
      get-fields-from-schema)))
